import { FormProvider } from "react-hook-form";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { Tab, Tabs } from "ui/tabs";
import { Typography } from "ui/typography";
import { BannerSection } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/sections/banner-section";
import { ButtonSection } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/sections/button-section";
import { LayoutSection } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/sections/layout-section";
import { TypographySection } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/sections/typography-section";
import { PreviewControls } from "containers/admin/clients/client/theme-configuration-page/theme-preview/preview-controls";
import { PreviewFrame } from "containers/admin/clients/client/theme-configuration-page/theme-preview/preview-frame";
import { PublishButton } from "containers/admin/clients/client/theme-configuration-page/components/publish-button/publish-button";

import { useEmailConfiguration } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/use-email-configuration";

import { getErrorMessage } from "utilities";

import {
  ACTIONS_SX,
  CONTENT_SX,
  ERROR_SX,
  PREVIEW_SX,
  SIDEBAR_SX,
  TAB_ERROR_SX,
  TAB_LABEL_SX,
  TOOLBAR_SX,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.styles";
import {
  EMAIL_CONFIGURATION_FORM_ID,
  EMAIL_CONFIGURATION_TABS,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.constants";
import { THEME_LOAD_ERROR_MESSAGE } from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.constants";

import Icons from "assets";

export const EmailConfiguration = () => {
  const {
    form,
    activeTab,
    tabsWithErrors,
    isEditing,
    isEditDisabled,
    isSaving,
    themeError,
    fieldsKey,
    beeFonts,
    colorOptions,
    srcDoc,
    screenSize,
    isBaseFontApplied,
    unsavedChangesDialog,
    setScreenSize,
    setIsBaseFontApplied,
    handleTabChange,
    handleEdit,
    handleCancel,
    handleSave,
  } = useEmailConfiguration();

  const disabled = !isEditing;

  return (
    <>
      <Box sx={TOOLBAR_SX}>
        <Tabs size="large" value={activeTab} onChange={handleTabChange} aria-label="Email configuration sections">
          {EMAIL_CONFIGURATION_TABS.map(({ value, label }) => (
            <Tab
              key={value}
              value={value}
              label={
                <Box component="span" sx={TAB_LABEL_SX}>
                  {label}
                  {tabsWithErrors.includes(value) && (
                    <Box component="span" sx={TAB_ERROR_SX}>
                      <Icons.ExclamationCircle role="img" aria-label="Has errors" />
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Box sx={ACTIONS_SX}>
          {isEditing ? (
            <>
              <Button variant="text" size="large" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="submit" form={EMAIL_CONFIGURATION_FORM_ID} size="large" loading={isSaving} disabled={isSaving}>
                Save changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" size="large" onClick={handleEdit} disabled={isEditDisabled}>
                Edit
              </Button>
              <PublishButton disabled={isEditDisabled} />
            </>
          )}
        </Box>
      </Box>
      {themeError ? (
        <Typography variant="body1" component="p" role="alert" sx={ERROR_SX}>
          {getErrorMessage(themeError) || THEME_LOAD_ERROR_MESSAGE}
        </Typography>
      ) : (
        <Box sx={CONTENT_SX}>
          <FormProvider {...form}>
            <Box
              key={fieldsKey}
              component="form"
              id={EMAIL_CONFIGURATION_FORM_ID}
              aria-label="Email configuration"
              noValidate
              onSubmit={handleSave}
              sx={SIDEBAR_SX}>
              {/* Hidden rather than unmounted: react-hook-form skips validating unmounted fields on submit. */}
              <Box hidden={activeTab !== "typography"}>
                <TypographySection beeFonts={beeFonts} colorOptions={colorOptions} disabled={disabled} />
              </Box>
              <Box hidden={activeTab !== "button"}>
                <ButtonSection colorOptions={colorOptions} disabled={disabled} />
              </Box>
              <Box hidden={activeTab !== "banner"}>
                <BannerSection disabled={disabled} />
              </Box>
              <Box hidden={activeTab !== "layout"}>
                <LayoutSection disabled={disabled} />
              </Box>
            </Box>
          </FormProvider>
          <Box sx={PREVIEW_SX}>
            <PreviewControls
              screenSize={screenSize}
              onScreenSizeChange={setScreenSize}
              isBaseFontApplied={isBaseFontApplied}
              onBaseFontChange={setIsBaseFontApplied}
            />
            <PreviewFrame title="Email theme preview" srcDoc={srcDoc} screenSize={screenSize} />
          </Box>
        </Box>
      )}
      {unsavedChangesDialog}
    </>
  );
};
